import { map, tap } from 'rxjs/operators';
import { HttpClient, HttpContextToken, HttpHeaders, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { forkJoin, Observable, Subject, throwError } from 'rxjs';
import { LogService } from '../services/log.service';
import { NgRedux } from '@angular-redux/store';

export const USE_ANONYMOUS_ACCESS_TOKEN = new HttpContextToken(() => false);

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private logoutBroadcast: Subject<boolean> = new Subject();
    private currentTermsToAccept: Subject<any> = new Subject();
    private acceptedToLatestTerms: Subject<any> = new Subject();
    public isAccountDeleted = false;

    currentTermsToAccept$ = this.currentTermsToAccept.asObservable();
    acceptedToLatestTerms$ = this.acceptedToLatestTerms.asObservable();
    logoutBroadcast$ = this.logoutBroadcast.asObservable();
    refreshedToken = '';
    cachedRequests: Array<HttpRequest<any>> = [];

    notify: Subject<any> = new Subject<any>();

    // https://ridango.atlassian.net/wiki/spaces/TEH/pages/137920513/Ridango+secure+OAuth+service+for+WEB+and+Mobile+interfaces+DRAFT
    constructor(
        @Inject('ENV') private env: any,
        private httpClient: HttpClient,
        private ls: LogService,
        private store: NgRedux<any>
    ) {}

    public collectFailedRequest(request): void {
        this.cachedRequests.push(request);
    }

    public emitLogoutBroadcast(redirect: boolean): void {
        this.logoutBroadcast.next(redirect);
    }

    refreshToken() {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/auth/refresh`;

        if (!this.env.IS_NEW_AFC) {
            const token = this.getLocalAccessToken();
            if (token) {
                const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
                return this.httpClient.post(url, {}, { headers });
            }
            return throwError(
                new HttpErrorResponse({
                    status: 401,
                    statusText: 'Unauthorized',
                    error: 'No token available for refresh'
                })
            );
        }

        return this.httpClient.post(url, {});
    }

    notifyRefreshFailure() {
        this.notify.next();
    }

    getAccessToken(
        provider: string,
        authorizationCode: string,
        termsVersion: any = null,
        channel: string = this.env.CHANNEL,
        lang: any = null
    ) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/auth`;
        let payload: any = {};
        payload = {
            type: provider,
            token: authorizationCode,
            region_mask: this.env.REGION_ID,
            channel: channel
        };

        if (termsVersion) {
            payload.terms_version = termsVersion;
        }

        if (lang) {
            payload.lang = lang;
        }

        return this.httpClient.post(url, payload).pipe(
            map((data) => {
                return data;
            })
        );
    }

    setLocalAccessToken(data: any, isCorporate = false) {
        const token = data.token;
        const email = data.email;
        const username = data.name;
        localStorage.setItem(`${this.env.APP_NAME}-access`, token);
        if (!isCorporate || !this.getLoggedInUserName()) {
            if (data.email) {
                localStorage.setItem(`${this.env.APP_NAME}-access-user`, email);
            }
            if (data.name) {
                localStorage.setItem(`${this.env.APP_NAME}-access-user`, username);
            }
            return;
        }
    }

    getAccountPersonalizationState(): boolean {
        return localStorage.getItem(`${this.env.APP_NAME}-account-personalized`) === 'true';
    }

    setAccountPersonalizationState(accountPersonalizationState: boolean): void {
        localStorage.setItem(`${this.env.APP_NAME}-account-personalized`, String(accountPersonalizationState));
    }

    getLocalAccessToken(): string {
        return localStorage.getItem(`${this.env.APP_NAME}-access`);
    }

    setLoggedInUserName(name: string) {
        localStorage.setItem(`${this.env.APP_NAME}-access-user`, name);
    }

    getLoggedInUserName() {
        return localStorage.getItem(`${this.env.APP_NAME}-access-user`);
    }

    getLoggedInEmail() {
        return localStorage.getItem(`${this.env.APP_NAME}-access-user-email`);
    }

    getLoginStatus() {
        if (localStorage.getItem(`${this.env.APP_NAME}-access`)) {
            return true;
        }
        return false;
    }

    signInWithUsernameAndPassword(username: string, password: string, lang: string): Observable<any> {
        const url = `${this.env.OAUTH_URL}/auth/oauth2/token`;
        const headers = new HttpHeaders({ 'Content-Language': lang });
        const payload = {
            grant_type: 'password',
            username: username,
            password: password,
            client_id: this.env.OAUTH_CLIENT_ID
        };
        return this.httpClient.post(url, payload, { headers: headers });
    }

    signOut(redirect: boolean = false) {
        localStorage.removeItem(`${this.env.APP_NAME}-access`);
        localStorage.removeItem(`${this.env.APP_NAME}-access-user`);
        localStorage.removeItem(`${this.env.APP_NAME}-access-user-email`);
        localStorage.removeItem(`${this.env.APP_NAME}-access-company-id`);
        localStorage.removeItem(`${this.env.APP_NAME}-account-personalized`);
        localStorage.removeItem(`${this.env.APP_NAME}-account-user-email`);
        this.isAccountDeleted = false;

        this.emitLogoutBroadcast(redirect);
    }

    signUpUserWithCredentials(credentials: any, terms: any, lang: string) {
        const headers = new HttpHeaders({ 'Content-Language': lang });
        const url = `${this.env.OAUTH_URL}/auth/oauth2/signup/${this.env.OAUTH_CLIENT_ID}`;
        let payload: any = {};
        payload = {
            username: credentials.email,
            password: credentials.password
        };
        if (terms) {
            payload = {
                username: credentials.email,
                password: credentials.password,
                terms_version: terms.id
            };
        }
        return this.httpClient.post(url, payload, { headers: headers });
    }

    verifyAccount(token: string) {
        const url = `${this.env.OAUTH_URL}/auth/oauth2/signup/verify/${this.env.OAUTH_CLIENT_ID}`;
        return this.httpClient.post(url, token);
    }

    sendResetEmail(email: any, lang: string) {
        const url = `${this.env.OAUTH_URL}/auth/oauth2/signup/reset/${this.env.OAUTH_CLIENT_ID}`;
        const headers = new HttpHeaders().set('Content-Language', lang);
        const options = {
            headers: headers
        };
        const payload = {
            username: email.email,
            password: ''
        };
        return this.httpClient.post(url, payload, options);
    }

    sendNewPassword(password: any, token: string, lang: string) {
        const url = `${this.env.OAUTH_URL}/auth/oauth2/signup/reset/${this.env.OAUTH_CLIENT_ID}`;
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`).set('Content-Language', lang);
        const options = {
            headers: headers
        };
        const payload = {
            username: '',
            password: password.password
        };
        return this.httpClient.post(url, payload, options);
    }

    putAgreeTerms(termsId, code) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/terms-and-conditions/agree`;
        const payload = {
            code: code,
            terms_id: termsId
        };
        return this.httpClient.put(url, payload);
    }

    setLoggedInUserCompanyId(companyId) {
        localStorage.setItem(`${this.env.APP_NAME}-access-company-id`, companyId);
    }

    getLoggedInUserCompanyId() {
        return JSON.parse(localStorage.getItem(`${this.env.APP_NAME}-access-company-id`));
    }

    removeLoggedInUserCompanyId() {
        localStorage.removeItem(`${this.env.APP_NAME}-access-company-id`);
    }

    changeLoggedUserLanguage(lang) {
        const token = this.getLocalAccessToken();

        if (token) {
            const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/customer-account/profile/lang`;
            const headers = new HttpHeaders()
                .set('Authorization', `Bearer ${token}`)
                .set('Content-Type', 'application/json; charset=utf-8');
            const options = {
                headers: headers
            };
            const payload = {
                lang: lang
            };
            return this.httpClient.put(url, payload, options);
        }
    }

    changePassword(payload) {
        // https://oauth.dev.ridango.com/auth/oauth2/change/password/sormland_web
        const url = `${this.env.OAUTH_URL}/auth/oauth2/change/password/${this.env.OAUTH_CLIENT_ID}`;

        const headers = new HttpHeaders()
            .set('Accept', `application/json`)
            .set('Content-Type', 'application/json; charset=utf-8');
        const options = {
            headers: headers
        };

        return this.httpClient.put(url, payload, options);
    }

    fetchAgreedTerms() {
        const token = this.getLocalAccessToken();
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        const options = {
            headers: headers
        };

        return this.httpClient
            .get<{
                contentBaseUrl: string;
                version: string;
                versionName: string;
            }>(`${this.env?.WMB?.WMB_PUBLIC_API}/v1/users/terms-and-conditions`, options)
            .pipe(
                tap((response) => {
                    return response;
                })
            );
    }

    fetchLatestTerms() {
        return this.httpClient
            .get<{
                contentBaseUrl: string;
                version: string;
                versionName: string;
            }>(`${this.env?.WMB?.WMB_PUBLIC_API}/v1/terms-and-conditions/latest`)
            .pipe(
                tap((response) => {
                    this.currentTermsToAccept.next(response);
                    return response;
                })
            );
    }

    hasUserAgreedToLatest(): Observable<boolean> {
        return forkJoin({
            agreedTerms: this.fetchAgreedTerms(),
            latestTerms: this.fetchLatestTerms()
        }).pipe(
            map(({ agreedTerms, latestTerms }) => {
                return (agreedTerms as any)?.version === (latestTerms as any)?.version;
            })
        );
    }

    agreeToLatestTerms(version: string): Observable<any> {
        const token = this.getLocalAccessToken();
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        const options = {
            headers: headers,
            params: {
                version: version
            }
        };

        this.acceptedToLatestTerms.next();
        return this.httpClient.put(`${this.env.WMB.WMB_PUBLIC_API}/v1/users/terms-and-conditions`, null, options);
    }

    buildTermsIframeUrl(termsUrl, language): string {
        const highContrast = this.store.getState().application.highContrast;
        return `${termsUrl}/${language}/${highContrast ? '#high-contrast' : ''}`;
    }
}
